import { useId } from "react";
import type { ReactNode } from "react";
import { VisuallyHidden } from "./VisuallyHidden";
import styles from "./Field.module.css";

/**
 * The labelled text, choice and consent controls the deal editor is built from.
 *
 * They exist as one family rather than four components so the accessible wiring — the part
 * that is easy to get subtly wrong and impossible to see — is written once:
 *
 *   - a real `<label>` bound by `id`, never a placeholder standing in for one;
 *   - hint **and** error both named in `aria-describedby`, so a screen reader reads the
 *     guidance and the problem rather than one or the other;
 *   - `aria-invalid` on the control, so the state is exposed rather than implied by a
 *     coloured border;
 *   - an error carrying a marker glyph and the word "Error", because canon
 *     `contracts/DESIGN-TOKENS.md` does not allow status by colour alone;
 *   - a 44px minimum target on every control.
 *
 * `AmountField` predates these and stays as it is: it is a money box with a currency
 * prefix, and folding it in would make one component that is two things. The visual
 * language is shared through the tokens, which is where sharing belongs.
 *
 * The id is generated per instance rather than derived from the field name, because a deal
 * editor can be open on more than one card — a name-derived id would repeat down the page
 * and quietly bind several labels to the same input.
 */

interface FieldShellProps {
  label: string;
  /** Spoken after the label, e.g. the product this field belongs to. */
  context?: string | undefined;
  hint?: ReactNode | undefined;
  error?: string | undefined;
  /** Stated in the label, so requiredness is not carried by an asterisk alone. */
  optional?: boolean | undefined;
}

interface Wiring {
  id: string;
  describedBy: string | undefined;
  invalid: true | undefined;
}

function useFieldWiring({ hint, error }: FieldShellProps): Wiring {
  const id = useId();
  const describedBy =
    [hint === undefined ? null : `${id}-hint`, error === undefined ? null : `${id}-error`]
      .filter(Boolean)
      .join(" ") || undefined;
  return { id, describedBy, invalid: error === undefined ? undefined : true };
}

function FieldLabel({
  htmlFor,
  label,
  context,
  optional,
}: { htmlFor: string } & Pick<FieldShellProps, "label" | "context" | "optional">) {
  return (
    <label className={styles.label} htmlFor={htmlFor}>
      {label}
      {context === undefined ? null : <VisuallyHidden> for {context}</VisuallyHidden>}
      {optional === true ? <span className={styles.optional}>Optional</span> : null}
    </label>
  );
}

function FieldMessages({
  id,
  hint,
  error,
}: { id: string } & Pick<FieldShellProps, "hint" | "error">) {
  return (
    <>
      {hint === undefined ? null : (
        <p className={styles.hint} id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p className={styles.error} id={`${id}-error`}>
          <svg className={styles.marker} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path
              d="M8 4.6v4.2M8 11v.9"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
          <VisuallyHidden>Error: </VisuallyHidden>
          {error}
        </p>
      )}
    </>
  );
}

export function TextField({
  value,
  onChange,
  disabled,
  type = "text",
  ...shell
}: FieldShellProps & {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean | undefined;
  /** `url` gets the right keyboard on a phone; validation stays core's. */
  type?: "text" | "url" | undefined;
}) {
  const { id, describedBy, invalid } = useFieldWiring(shell);
  return (
    <div className={styles.field}>
      <FieldLabel htmlFor={id} {...shell} />
      <input
        id={id}
        className={styles.input}
        type={type}
        value={value}
        disabled={disabled}
        autoComplete="off"
        aria-describedby={describedBy}
        aria-invalid={invalid}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <FieldMessages id={id} {...shell} />
    </div>
  );
}

export function TextAreaField({
  value,
  onChange,
  disabled,
  rows = 3,
  ...shell
}: FieldShellProps & {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean | undefined;
  rows?: number | undefined;
}) {
  const { id, describedBy, invalid } = useFieldWiring(shell);
  return (
    <div className={styles.field}>
      <FieldLabel htmlFor={id} {...shell} />
      <textarea
        id={id}
        className={`${styles.input} ${styles.textarea}`}
        rows={rows}
        value={value}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      />
      <FieldMessages id={id} {...shell} />
    </div>
  );
}

export function SelectField({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  ...shell
}: FieldShellProps & {
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  /** Offered only when the current value is empty, so it cannot be chosen back into. */
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
}) {
  const { id, describedBy, invalid } = useFieldWiring(shell);
  return (
    <div className={styles.field}>
      <FieldLabel htmlFor={id} {...shell} />
      <select
        id={id}
        className={`${styles.input} ${styles.select}`}
        value={value}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        {value === "" && placeholder !== undefined ? (
          <option value="">{placeholder}</option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <FieldMessages id={id} {...shell} />
    </div>
  );
}

/**
 * A consent-shaped control: the label sits beside the box and is bound to it, so the whole
 * sentence is the target rather than a 16px square.
 */
export function CheckboxField({
  checked,
  onChange,
  disabled,
  label,
  context,
  hint,
  error,
}: Omit<FieldShellProps, "optional"> & {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean | undefined;
}) {
  const { id, describedBy, invalid } = useFieldWiring({ label, hint, error });
  return (
    <div className={styles.field}>
      <div className={styles.check}>
        <input
          id={id}
          className={styles.checkbox}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={invalid}
          onChange={(event) => {
            onChange(event.target.checked);
          }}
        />
        <label className={styles.checkLabel} htmlFor={id}>
          {label}
          {context === undefined ? null : <VisuallyHidden> for {context}</VisuallyHidden>}
        </label>
      </div>
      <FieldMessages id={id} {...{ hint, error }} />
    </div>
  );
}
